import {
  DataItem,
  InteractionsTrigger,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { CompRef } from '@wix/document-services-types';
import { NativeActionsList } from '@wix/editor-platform-host-integration-apis';
import { DATA, Action, CONTAINER_TYPE } from '@wix/component-protocol/schema';
import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import {
  hasDataType,
  isCustomizedNativeManifestAction,
  isSingleSelection,
} from '../../utils';

import type { EditorPointer } from '@wix/editor-document-logic-api';

const INTERACTION_TRIGGERS = Object.values(InteractionsTrigger);

const validateNativeAction = (action?: Action) => {
  return !!action && !isCustomizedNativeManifestAction(action);
};

export const validateInteractionAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  if (editorPointers.length === 0) {
    return false;
  }

  return editorPointers.every((pointer) => {
    const interactions = componentsAPI.components.getInteractions(pointer);
    if (!interactions?.triggers?.length) {
      return false;
    }
    return interactions.triggers.every((trigger) =>
      INTERACTION_TRIGGERS.includes(trigger),
    );
  });
};

export const validateMediaAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const mediaAction =
    componentsAPI.components.getEditActions(editorPointers)?.media;
  const hasMedia = componentsAPI.editActions.hasActionableDataItems(
    NativeActionsList.changeMedia,
    editorPointers,
  );
  return validateNativeAction(mediaAction) || (!mediaAction && hasMedia);
};

export const validateHelpAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  if (!isSingleSelection(editorPointers)) {
    return false;
  }
  const helpData = componentsAPI.components.getHelp(editorPointers[0]);
  return Boolean(helpData?.kbId || helpData?.url);
};

export const validateEditImageAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const mediaAction =
    componentsAPI.components.getEditActions(editorPointers)?.media;
  const hasMedia = componentsAPI.editActions.hasActionableDataItems(
    NativeActionsList.editImage,
    editorPointers,
  );
  return validateNativeAction(mediaAction) || (!mediaAction && hasMedia);
};

export const validateCropImageAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  return componentsAPI.editActions.hasActionableDataItems(
    NativeActionsList.cropImage,
    editorPointers,
  );
};

export const validateSettingsAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const settingsAction =
    componentsAPI.components.getEditActions(editorPointers)?.settings;
  const hasData = componentsAPI.editActions.hasActionableDataItems(
    NativeActionsList.settings,
    editorPointers,
  );
  return validateNativeAction(settingsAction) || (!settingsAction && hasData);
};

export const validateManageMenuAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const manageMenuAction =
    componentsAPI.components.getEditActions(editorPointers)?.manageMenu;
  const hasData = componentsAPI.editActions.hasActionableDataItems(
    NativeActionsList.manageMenu,
    editorPointers,
  );

  return (
    validateNativeAction(manageMenuAction) || (!manageMenuAction && hasData)
  );
};

export const validateManageItemsAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const manageItemsAction =
    componentsAPI.components.getEditActions(editorPointers)?.manageItems;
  const hasData = componentsAPI.editActions.hasActionableDataItems(
    NativeActionsList.manageItems,
    editorPointers,
  );
  return (
    validateNativeAction(manageItemsAction) || (!manageItemsAction && hasData)
  );
};

export const validateOpacityAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const style = componentsAPI.components.getStyle(editorPointers);
  if (!style) {
    return false;
  }
  const { cssProperties, cssCustomProperties } = style;

  return (
    cssProperties?.opacity !== undefined ||
    Object.values(cssCustomProperties ?? {}).some(
      (value) => value?.cssPropertyType === 'opacity',
    )
  );
};

export const validateDesignAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const designAction =
    componentsAPI.components.getEditActions(editorPointers)?.design;

  const hasStyleDefinitions =
    componentsAPI.editActions.hasActionableStyleProperties(editorPointers);

  const hasDataDefinitions = componentsAPI.editActions.hasActionableDataItems(
    NativeActionsList.design,
    editorPointers,
  );

  const hasDesignDefinitions = hasStyleDefinitions || hasDataDefinitions;

  return (
    validateNativeAction(designAction) ||
    (!designAction && hasDesignDefinitions)
  );
};

export const validateGlobalColorsStylesAction = (
  _componentsAPI: ComponentsPlatformService,
  _editorPointers: EditorPointer<CompRef>[],
) => {
  // Always return true as global colors and styles action
  // Additional validation is done on the editor side (shouldBeExcluded)
  return true;
};

export const validateRichTextAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) =>
  hasDataType(
    componentsAPI.components.getData(editorPointers),
    DATA.DATA_TYPE.richText,
  );

export const validatePluginsAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
): boolean => {
  if (!isSingleSelection(editorPointers)) {
    return false;
  }

  const isDataItemsHaveSlot = (dataItems?: Record<string, DataItem>) => {
    if (!dataItems) {
      return false;
    }

    return Object.values(dataItems).some(
      (dataItem) =>
        dataItem.dataType === DATA.DATA_TYPE.container &&
        dataItem.container?.containerType === CONTAINER_TYPE.slot,
    );
  };

  const api = componentsAPI.buildManifestAPI({
    editorPointers,
  });
  const rootDataDefinitions = componentsAPI.components.getData(editorPointers);
  if (isDataItemsHaveSlot(rootDataDefinitions as Record<string, DataItem>)) {
    return true;
  }

  return api.getChildren().some((child) => {
    const childDataDefinitions = child.resolver.getDataDefinitions();
    return isDataItemsHaveSlot(
      childDataDefinitions as Record<string, DataItem>,
    );
  });
};

export const validateDesignStatesAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const designStates =
    componentsAPI.states.getDesignStateDefinitions(editorPointers);
  return Object.keys(designStates).length > 0;
};

export const validatePresetsAction = (
  componentsAPI: ComponentsPlatformService,
  editorPointers: EditorPointer<CompRef>[],
) => {
  const presets = componentsAPI.editActions.getActionPresets(
    NativeActionsList.presets,
    editorPointers,
  );

  const validPresetsKeys = Object.entries(presets)
    .filter(([_, presetDefinition]) => Boolean(presetDefinition?.thumbnailUrl))
    .map(([presetKey, _]) => presetKey);

  if (isSingleSelection(editorPointers)) {
    return (
      componentsAPI.editActions.hasTemplates(editorPointers[0]) ||
      validPresetsKeys.length > 1
    );
  }

  // Product: in order to have MORE designs, component needs to have more than one preset (= design)
  return validPresetsKeys.length > 1;
};
